/////////////////////////////////////////////////////////////////////////////////////////////////////////
//  1. 프로그램명 : 팝업 크기조절(공통)
//  2. 프로그램ID : ResizablePopup.tsx
//  3. 주요설명 : 화면 가운데 정렬된 팝업의 오른쪽 아래 모서리를 끌어서 크기를 조절함(2026-10-18).
//               - useResizable : 공통팝업 틀(CommonModal의 PopupFrame/공통팝업)에서 사용
//               - ResizablePaper : MUI Dialog 전체(App.tsx 테마 MuiDialog defaultProps.PaperComponent)에 적용
//               가운데 정렬이라 팝업이 양쪽으로 같이 커지므로 마우스 이동량의 2배만큼 크기를 바꿔야 모서리가 마우스를 따라옴.
//               바꾼 크기는 기억하지 않음(팝업을 닫으면 원래 크기). 전체화면(fullScreen) Dialog는 대상 아님.
//  4. 개발이력 : 2026.10.18  최초개발
/////////////////////////////////////////////////////////////////////////////////////////////////////////
import React, { useCallback, useRef, useState } from 'react';
import Paper, { type PaperProps } from '@mui/material/Paper';

const clamp = (v: number, min: number, max: number) => Math.max(min, Math.min(max, v));

// 모서리 손잡이(오른쪽 아래, 대각선 무늬)
const ResizeHandle: React.FC<{ onPointerDown: (e: React.PointerEvent) => void }> = ({ onPointerDown }) => (
    <div
        className="popup-resize-handle"
        title="크기 조절"
        onPointerDown={onPointerDown}
        style={{
            position: 'absolute', right: 0, bottom: 0, width: 16, height: 16, zIndex: 5, cursor: 'nwse-resize', touchAction: 'none',
            background: 'linear-gradient(135deg, transparent 0 55%, #94a3b8 55% 62%, transparent 62% 72%, #94a3b8 72% 79%, transparent 79%)',
        }}
    />
);

/**
 * 가운데 정렬 팝업 크기조절 훅
 * @param minW/minH : 최소 크기(px)
 * @param pad : 화면 가장자리 여유(px) - 최대 크기 = 창 크기 - pad
 * @returns ref(팝업 바깥 요소에 연결), sizeStyle(조절한 크기, 없으면 undefined), resetSize, handle(모서리 손잡이 - 팝업 안에 넣음, 팝업은 position:relative)
 */
export function useResizable(minW = 320, minH = 200, pad = 16) {
    const ref = useRef<HTMLElement | null>(null);
    const [size, setSize] = useState<{ w: number; h: number } | null>(null);
    const onPointerDown = useCallback((e: React.PointerEvent) => {
        const el = ref.current;
        if (!el || e.button !== 0) return;
        e.preventDefault();
        e.stopPropagation();
        const r = el.getBoundingClientRect();
        const sx = e.clientX, sy = e.clientY;
        const maxW = window.innerWidth - pad, maxH = window.innerHeight - pad;
        const move = (ev: PointerEvent) => setSize({
            w: Math.round(clamp(r.width + (ev.clientX - sx) * 2, minW, maxW)),
            h: Math.round(clamp(r.height + (ev.clientY - sy) * 2, minH, maxH)),
        });
        const up = () => {
            window.removeEventListener('pointermove', move);
            window.removeEventListener('pointerup', up);
            document.body.style.cursor = '';
            document.body.style.userSelect = '';
        };
        window.addEventListener('pointermove', move);
        window.addEventListener('pointerup', up);
        document.body.style.cursor = 'nwse-resize';
        document.body.style.userSelect = 'none';
    }, [minW, minH, pad]);
    const sizeStyle: React.CSSProperties | undefined = size ? { width: size.w, height: size.h, maxWidth: 'none', maxHeight: 'none' } : undefined;
    const resetSize = useCallback(() => setSize(null), []);
    return { ref, sizeStyle, resetSize, handle: <ResizeHandle onPointerDown={onPointerDown} /> };
}

// MUI Dialog용 Paper - 앱 전체 Dialog에 테마로 적용(App.tsx). Dialog 여백(32px x 2)만큼 최대 크기에서 뺌
export const ResizablePaper = React.forwardRef<HTMLDivElement, PaperProps>(function ResizablePaper(props, ref) {
    const { ref: boxRef, sizeStyle, handle } = useResizable(320, 160, 64);
    const fullScreen = String(props.className || '').includes('FullScreen');
    const setRefs = useCallback((el: HTMLDivElement | null) => {
        boxRef.current = el;
        if (typeof ref === 'function') ref(el);
        else if (ref) (ref as React.MutableRefObject<HTMLDivElement | null>).current = el;
    }, [ref, boxRef]);
    return (
        <Paper {...props} ref={setRefs} style={{ ...props.style, ...(fullScreen ? null : sizeStyle) }}>
            {props.children}
            {!fullScreen && handle}
        </Paper>
    );
});
